import React, { useState, useRef, Fragment, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 *
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 * Content slices are taken from the source canvas one page-height at a time
 * so rows never get clipped mid-content across page breaks.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  // A4 geometry (millimetres)
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  // How many source pixels equal one printed content page (in canvas pixels)?
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  // Slice the source canvas page-by-page; render each slice into its own
  // temporary canvas, paint that into the PDF. This avoids the "shift the
  // whole image up" trick that clips rows mid-cell.
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
type ViewingTest = {
  kind: 'lab';
  encounterId: string;
  labId: string;
} | {
  kind: 'rad';
  encounterId: string;
  radId: string;
} | null;
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const navigate = useNavigate();
  // Amendment modal — Clinicians can reopen a completed encounter, provide a
  // required reason, and the Clinician Dashboard picks up that context on
  // arrival to log audit events on every save.
  const [amendModal, setAmendModal] = useState<{
    isOpen: boolean;
    encounterId: string;
    reason: string;
  }>({
    isOpen: false,
    encounterId: '',
    reason: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [viewingTest, setViewingTest] = useState<ViewingTest>(null);
  const [imageZoom, setImageZoom] = useState<number>(100); // percentage 10-200
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const testModalRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── Test File modal helpers ───
  const closeTestModal = () => {
    setViewingTest(null);
    setImageZoom(100);
  };
  const handlePrintTestModal = () => {
    // Use a print-only class on body so only the modal prints
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${selectedPatient?.bmcFileNumber || 'patient'}_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-256">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-257">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-258">Patient File</h1>
          <div className="flex space-x-2" data-id="element-259">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-260">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-261">
              <Printer className="w-4 h-4 mr-2" data-id="element-262" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-263">
              <Download className="w-4 h-4 mr-2" data-id="element-264" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-265">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-266" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-267">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-268">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-269">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-270">
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-271">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-272">
                {/* Clinician-only Amend action for completed visits */}
                {currentUser?.role === 'Clinician' && enc.stage === 'COMPLETED' && <div className="print:hidden flex justify-end mb-3" data-id="element-273">
                      <Button variant="outline" className="text-amber-700 border-amber-400 hover:bg-amber-50" onClick={() => setAmendModal({
              isOpen: true,
              encounterId: enc.id,
              reason: ''
            })} data-id="element-274">
                        <FileText className="w-4 h-4 mr-2" data-id="element-275" />
                        Amend File
                      </Button>
                    </div>}
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-276">
                  <div className="flex items-center" data-id="element-277">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-278" />
                    <div data-id="element-279">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-280">
                        BMED Medical Centre
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-281">
                        Maternity Care Made Better
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-282">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-283">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-284">
                      <span className="font-semibold" data-id="element-285">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-286">
                      <span className="font-semibold" data-id="element-287">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-288">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-289">
                    <div className="col-span-1 font-semibold" data-id="element-290">1.</div>
                    <div className="col-span-6" data-id="element-291">
                      <span className="font-semibold" data-id="element-292">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-293">
                      <span className="font-semibold" data-id="element-294">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-295">
                      <span className="font-semibold" data-id="element-296">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-297">
                    <div className="col-span-1 font-semibold" data-id="element-298">2.</div>
                    <div className="col-span-11" data-id="element-299">
                      <span className="font-semibold" data-id="element-300">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-301">
                    <div className="col-span-1 font-semibold" data-id="element-302">3.</div>
                    <div className="col-span-7" data-id="element-303">
                      <span className="font-semibold" data-id="element-304">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-305">
                      <span className="font-semibold" data-id="element-306">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-307">
                    <div className="col-span-1 font-semibold" data-id="element-308">4.</div>
                    <div className="col-span-11" data-id="element-309">
                      <span className="font-semibold" data-id="element-310">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-311">
                    <div className="col-span-1 font-semibold" data-id="element-312">5.</div>
                    <div className="col-span-5" data-id="element-313">
                      <span className="font-semibold" data-id="element-314">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-315">
                      <span className="font-semibold" data-id="element-316">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-317">
                      <span className="font-semibold" data-id="element-318">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-319">
                    <div className="col-span-1 font-semibold" data-id="element-320">6.</div>
                    <div className="col-span-11" data-id="element-321">
                      <span className="font-semibold" data-id="element-322">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-323">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-324">
                    <div className="col-span-1 font-semibold" data-id="element-325">7.</div>
                    <div className="col-span-11" data-id="element-326">
                      <span className="font-semibold" data-id="element-327">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-328">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-329">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-330">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-331">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-332">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-333">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-334">
                    <thead data-id="element-335">
                      <tr className="bg-gray-50" data-id="element-336">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-337">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-338">
                        <th className="border border-black p-1" data-id="element-339">Temp</th>
                        <th className="border border-black p-1" data-id="element-340">Pulse</th>
                        <th className="border border-black p-1" data-id="element-341">Resp</th>
                        <th className="border border-black p-1" data-id="element-342">BP</th>
                        <th className="border border-black p-1" data-id="element-343">SpO2</th>
                        <th className="border border-black p-1" data-id="element-344">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-345">Height</th>
                        <th className="border border-black p-1" data-id="element-346">BMI</th>
                        <th className="border border-black p-1" data-id="element-347">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-348">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-349">
                      <tr data-id="element-350">
                        <td className="border border-black p-2 font-medium" data-id="element-351">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-352">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-353">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-354">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-355">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-356">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-357">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-358">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-359">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-360">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-361">
                  <div className="border-2 border-black" data-id="element-362">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-363">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-364">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-365">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-366">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-367">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-368">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-369">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-370">
                      <tbody data-id="element-371">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-372">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-373">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-374">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-375">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-376">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-377">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-378">
                      <span className="font-semibold" data-id="element-379">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-380">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-381">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-382">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-383">
                      <tbody data-id="element-384">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-385">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-386">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-387">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-388">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-389">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-390">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-391">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-392">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-393">
                      <tbody data-id="element-394">
                        <tr className="border-b border-gray-300" data-id="element-395">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-396">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-397">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-398">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-399">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-400">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-401">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-402">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-403">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-404">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-405">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-406">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-407">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-408">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-409">
                      <p data-id="element-410">
                        <span className="font-semibold" data-id="element-411">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-412">
                        <span className="font-semibold" data-id="element-413">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-414">
                        <span className="font-semibold" data-id="element-415">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-416">
                        <span className="font-semibold" data-id="element-417">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-418">
                        <span className="font-semibold" data-id="element-419">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-420">
                        <span className="font-semibold" data-id="element-421">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-422">
                        <span className="font-semibold" data-id="element-423">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-424">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-425">
                      On Examination
                    </h3>
                    <div className="p-3 space-y-2 text-sm" data-id="element-426">
                      {([{
                  key: 'gc',
                  label: 'G/C'
                }, {
                  key: 'febrile',
                  label: 'Temperature'
                }, {
                  key: 'pjc',
                  label: 'P | J | C'
                }, {
                  key: 's1s2',
                  label: 'S1  S2'
                }, {
                  key: 'rs',
                  label: 'R/S'
                }, {
                  key: 'pa',
                  label: 'P/A'
                }] as const).map(field => <div key={field.key} className="grid grid-cols-[6rem_1fr] gap-2" data-id="element-427">
                          <span className="font-bold" data-id="element-428">{field.label}:</span>
                          <span className="border-b border-gray-300 pb-0.5" data-id="element-429">
                            {enc.clinicalNote?.onExaminationDetails?.[field.key] || '\u00A0'}
                          </span>
                        </div>)}
                      {enc.clinicalNote?.onExamination && <div className="pt-2 mt-2 border-t border-gray-200" data-id="element-430">
                          <p className="text-xs font-bold uppercase text-gray-600 mb-1" data-id="element-431">
                            Additional Notes
                          </p>
                          <p className="whitespace-pre-wrap" data-id="element-432">
                            {enc.clinicalNote.onExamination}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-433">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-434">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-435">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-436">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-437">{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-438">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-439">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-440">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-441">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-442">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-443">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-444">
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-445">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-446">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-447">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-448">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-449">
                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-450">
                        Laboratory Tests
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-451">
                        <tbody data-id="element-452">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-453">
                              {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-454">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-455"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-456"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-457">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-458">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-459">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-460">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-461">
                        Radiology Scans
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-462">
                        <tbody data-id="element-463">
                          {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-464">
                              {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-465">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-466"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-467"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a);
                        });
                        return <Fragment key={scan} data-id="element-468">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-469">
                                      {scan}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-470">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-471">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-472">
                        <span className="font-semibold" data-id="element-473">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results — View File list */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-474">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-475">
                            Investigation Results
                          </h4>
                          <table className="w-full border-collapse text-sm" data-id="element-476">
                            <thead data-id="element-477">
                              <tr className="bg-gray-100" data-id="element-478">
                                <th className="border border-black p-1.5 text-left" data-id="element-479">
                                  Test / Scan
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-480">
                                  Type
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-481">
                                  Performed By
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-482">
                                  Completed
                                </th>
                                <th className="border border-black p-1.5 text-center w-32 print:hidden" data-id="element-483">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-484">
                              {enc.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(testName => !(lab.removedTestNames || []).includes(testName)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-485">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-486">
                                          {testName}
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-487">
                                          Laboratory
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-488">
                                          —
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-489">
                                          {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                        </td>
                                        <td className="border border-black p-1.5 text-center print:hidden" data-id="element-490">
                                          <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id,
                            testName
                          })} data-id="element-491">
                                            <Eye className="w-3.5 h-3.5 mr-1" data-id="element-492" />{' '}
                                            View File
                                          </Button>
                                        </td>
                                      </tr>))}
                              {enc.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-493">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-494">
                                      {lab.otherTests}{' '}
                                      <span className="text-xs text-gray-500" data-id="element-495">
                                        (Other)
                                      </span>
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-496">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-497">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-498">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-499">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id
                          })} data-id="element-500">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-501" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                              {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-502">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-503">
                                      {rad.scanType}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-504">
                                      Radiology
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-505">
                                      {rad.radiographerName || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-506">
                                      {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-507">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            encounterId: enc.id,
                            radId: rad.id
                          })} data-id="element-508">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-509" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-510">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-511">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-512">
                      <thead data-id="element-513">
                        <tr className="bg-gray-100" data-id="element-514">
                          <th className="border border-black p-1.5 w-10" data-id="element-515">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-516">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-517">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-518">Route</th>
                          <th className="border border-black p-1.5" data-id="element-519">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-520">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-521">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-522">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-523">
                                <td className="border border-black p-1.5" data-id="element-524">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-525">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-526">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-527">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-528">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-529">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-530">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-531">
                                <td className="border border-black p-3" data-id="element-532">{i}</td>
                                <td className="border border-black p-3" data-id="element-533"></td>
                                <td className="border border-black p-3" data-id="element-534"></td>
                                <td className="border border-black p-3" data-id="element-535"></td>
                                <td className="border border-black p-3" data-id="element-536"></td>
                                <td className="border border-black p-3" data-id="element-537"></td>
                                <td className="border border-black p-3" data-id="element-538"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-539">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-540">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-541">
                        <thead data-id="element-542">
                          <tr className="bg-gray-100" data-id="element-543">
                            <th className="border border-black p-1.5 w-10" data-id="element-544">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-545">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-546">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-547">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-548">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-549">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-550">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-551">
                              <td className="border border-black p-1.5 text-center" data-id="element-552">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-553">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-554">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-555">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-556">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-557">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-558">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-559">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-560">
                      <thead data-id="element-561">
                        <tr className="bg-gray-100" data-id="element-562">
                          <th className="border border-black p-1 w-10" data-id="element-563">No.</th>
                          <th className="border border-black p-1" data-id="element-564">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-565">No.</th>
                          <th className="border border-black p-1" data-id="element-566">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-567">No.</th>
                          <th className="border border-black p-1" data-id="element-568">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-569">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-570">
                            <td className="border border-black p-1 font-medium" data-id="element-571">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-572">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-573">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-574"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-575">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-576"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-577">
                    <p className="font-bold text-sm" data-id="element-578">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-579">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-580">
              <p className="text-gray-500 text-center py-8" data-id="element-581">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>

        {/* Amendment Reason Modal */}
        {amendModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-582">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-583">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-584">
                Amend Completed Visit
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-585">
                Please provide a reason for amending this completed file. Your
                name, the timestamp, and this reason will be recorded in the
                audit trail.
              </p>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none" rows={4} value={amendModal.reason} onChange={e => setAmendModal({
            ...amendModal,
            reason: e.target.value
          })} placeholder="e.g., Correction of working diagnosis after MDT discussion" data-id="element-586" />
              <div className="flex justify-end space-x-3 mt-4" data-id="element-587">
                <Button variant="outline" onClick={() => setAmendModal({
              isOpen: false,
              encounterId: '',
              reason: ''
            })} data-id="element-588">
                  Cancel
                </Button>
                <Button variant="primary" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!amendModal.reason.trim()} onClick={() => {
              navigate('/dashboard', {
                state: {
                  amendEncounterId: amendModal.encounterId,
                  amendReason: amendModal.reason.trim()
                }
              });
            }} data-id="element-589">
                  Open in Consultation
                </Button>
              </div>
            </div>
          </div>}

        {viewingTest && (() => {
        const enc = encounters.find(e => e.id === viewingTest.encounterId);
        if (!enc) return null;
        const lab = viewingTest.kind === 'lab' ? enc.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? enc.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-590">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-591">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-592">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-593">
                      {lab ? 'Laboratory Result' : 'Radiology Result'}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-594">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-595">
                        <Printer className="w-4 h-4 mr-1" data-id="element-596" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-597">
                        <Download className="w-4 h-4 mr-1" data-id="element-598" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-599">
                        <X className="w-5 h-5 text-gray-500" data-id="element-600" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-601">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-602">
                      <div className="flex items-center" data-id="element-603">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-604" />
                        <div data-id="element-605">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-606">
                            BMED Medical Centre
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-607">
                            Maternity Care Made Better
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-608">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-609">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-610">
                          <span className="font-semibold" data-id="element-611">Patient:</span>{' '}
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p data-id="element-612">
                          <span className="font-semibold" data-id="element-613">File No:</span>{' '}
                          {selectedPatient.bmcFileNumber}
                        </p>
                        <p data-id="element-614">
                          <span className="font-semibold" data-id="element-615">Age/Sex:</span>{' '}
                          {selectedPatient.age} / {selectedPatient.sex}
                        </p>
                        <p data-id="element-616">
                          <span className="font-semibold" data-id="element-617">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-618">
                            <span className="font-semibold" data-id="element-619">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && (() => {
                const focusedTest = viewingTest?.kind === 'lab' ? viewingTest.testName : undefined;
                const showFBC = focusedTest && isFBCTest(focusedTest);
                const showUA = focusedTest && isUrinalysisTest(focusedTest);
                const showT = focusedTest && isTestosteroneTest(focusedTest);
                const showHP = focusedTest && isHormonalProfileTest(focusedTest);
                const showAMH = focusedTest && isAMHTest(focusedTest);
                const showHCG = focusedTest && isHCGTest(focusedTest);
                return <div data-id="element-620">
                            <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-621">
                              {focusedTest || 'Tests Performed'}
                            </h2>
                            {showFBC ? <div className="space-y-4" data-id="element-622">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-623">
                                  <h3 className="font-bold uppercase" data-id="element-624">
                                    Full Blood Count (FBC) Report
                                  </h3>
                                </div>
                                {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-625">
                                    <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-626">
                                      {cat.category}
                                    </div>
                                    <table className="w-full border-collapse text-sm" data-id="element-627">
                                      <thead data-id="element-628">
                                        <tr className="bg-gray-50" data-id="element-629">
                                          <th className="border border-black p-1.5 text-left" data-id="element-630">
                                            Test
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-631">
                                            Result
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-632">
                                            Reference Range
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-24" data-id="element-633">
                                            Unit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-634">
                                        {cat.tests.map(t => <tr key={t.name} data-id="element-635">
                                            <td className="border border-black p-1.5" data-id="element-636">
                                              {t.name}
                                            </td>
                                            <td className="border border-black p-1.5 font-bold" data-id="element-637">
                                              {lab.results && lab.results[t.name] || '—'}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-638">
                                              {t.range}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-639">
                                              {t.unit}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>)}
                              </div> : showUA ? <div data-id="element-640">
                                <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-641">
                                  <h3 className="font-bold uppercase" data-id="element-642">
                                    Urinalysis Report
                                  </h3>
                                </div>
                                <div className="max-w-md mx-auto space-y-2" data-id="element-643">
                                  {urinalysisTests.map(t => {
                        const key = `UA_${t.name}`;
                        const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                        return <div key={t.name} className="flex items-center text-sm" data-id="element-644">
                                        <span className="w-16 font-bold" data-id="element-645">
                                          {t.name}
                                        </span>
                                        <span className="mx-3 text-gray-400" data-id="element-646">
                                          -
                                        </span>
                                        <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-647">
                                          {value || '\u00A0'}
                                        </span>
                                      </div>;
                      })}
                                </div>
                                <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-648">
                                  <div data-id="element-649">
                                    <p className="font-semibold mb-2" data-id="element-650">
                                      Dr.'s Name:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-651">
                                      {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                    </p>
                                  </div>
                                  <div data-id="element-652">
                                    <p className="font-semibold mb-2" data-id="element-653">
                                      Signature:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-654">
                                      {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                    </p>
                                  </div>
                                </div>
                              </div> : showT ? <div className="space-y-2" data-id="element-655">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-656">
                                  <h3 className="font-bold uppercase" data-id="element-657">
                                    Testosterone Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-658">
                                  <thead data-id="element-659">
                                    <tr className="bg-gray-100" data-id="element-660">
                                      <th className="border border-black p-1.5 text-left" data-id="element-661">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-662">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-663">
                                        Reference (Male)
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-664">
                                        Reference (Female)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-665">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-666">
                                    {testosteroneTests.map(t => <tr key={t.name} data-id="element-667">
                                        <td className="border border-black p-1.5" data-id="element-668">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-669">
                                          {lab.results && lab.results[`TESTO_${t.name}`] || lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-670">
                                          {t.rangeMale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-671">
                                          {t.rangeFemale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-672">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div> : showHP ? <div data-id="element-673">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-674">
                                  <h3 className="font-bold uppercase" data-id="element-675">
                                    Hormonal Profile Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-676">
                                  <thead data-id="element-677">
                                    <tr className="bg-gray-100" data-id="element-678">
                                      <th className="border border-black p-1.5 text-left" data-id="element-679">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-680">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-681">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-682">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-683">
                                    {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-684">
                                        <tr className="align-top" data-id="element-685">
                                          <td className="border border-black p-1.5 font-medium" data-id="element-686">
                                            {h.name}
                                          </td>
                                          <td className="border border-black p-1.5 font-bold" data-id="element-687">
                                            {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-688">
                                            {h.ranges.map(r => <div key={r.label} data-id="element-689">
                                                <span className="font-medium" data-id="element-690">
                                                  {r.label}:
                                                </span>{' '}
                                                {r.value}
                                              </div>)}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-691">
                                            {h.unit}
                                          </td>
                                        </tr>
                                        <tr data-id="element-692">
                                          <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-693">
                                            Method: {h.method}
                                          </td>
                                        </tr>
                                      </Fragment>)}
                                  </tbody>
                                </table>
                              </div> : showAMH ? <div data-id="element-694">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-695">
                                  <h3 className="font-bold uppercase" data-id="element-696">
                                    Anti Mullerian Hormone (AMH) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-697">
                                  <thead data-id="element-698">
                                    <tr className="bg-gray-100" data-id="element-699">
                                      <th className="border border-black p-1.5 text-left" data-id="element-700">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-701">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-702">
                                        Reference Range (by age, ng/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-703">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-704">
                                    <tr className="align-top" data-id="element-705">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-706">
                                        {amhStructure.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-707">
                                        {lab.results && (lab.results[amhStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['ANTI-MÜLLERIAN HORMONE'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-708">
                                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-709">
                                            <span className="font-medium" data-id="element-710">
                                              {a.ageBand}:
                                            </span>{' '}
                                            {a.range}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-711">
                                        {amhStructure.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-712">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-713">
                                        Method: {amhStructure.method}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-714">
                                  <p className="font-bold uppercase mb-1" data-id="element-715">
                                    Interpretation
                                  </p>
                                  <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-716">
                                    {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-717">{line}</li>)}
                                  </ul>
                                </div>
                              </div> : showHCG ? <div data-id="element-718">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-719">
                                  <h3 className="font-bold uppercase" data-id="element-720">
                                    Beta HCG (Quantitative) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-721">
                                  <thead data-id="element-722">
                                    <tr className="bg-gray-100" data-id="element-723">
                                      <th className="border border-black p-1.5 text-left" data-id="element-724">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-725">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-726">
                                        Reference Range (mIU/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-727">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-728">
                                    {(() => {
                          const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                          const abnormal = isHCGAbnormal(hcgValue);
                          return <>
                                          <tr className="align-top" data-id="element-729">
                                            <td className="border border-black p-1.5 font-medium" data-id="element-730">
                                              {hcgStructure.name}
                                            </td>
                                            <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-731">
                                              {hcgValue || '—'}
                                              {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-732">
                                                  Above normal threshold (&gt; 6
                                                  mIU/mL)
                                                </div>}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-733">
                                              {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-734">
                                                  <span className="font-medium" data-id="element-735">
                                                    {r.label}:
                                                  </span>{' '}
                                                  {r.range}
                                                </div>)}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-736">
                                              {hcgStructure.unit}
                                            </td>
                                          </tr>
                                          <tr data-id="element-737">
                                            <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-738">
                                              Method: {hcgStructure.method}
                                            </td>
                                          </tr>
                                        </>;
                        })()}
                                  </tbody>
                                </table>
                              </div> : <>
                                <p className="mb-4 text-sm" data-id="element-739">
                                  <span className="font-semibold" data-id="element-740">
                                    Tests Ordered:
                                  </span>{' '}
                                  {lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}
                                  {lab.otherTests ? `, ${lab.otherTests}` : ''}
                                </p>
                                {lab.results && Object.keys(lab.results).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-741">
                                    <thead data-id="element-742">
                                      <tr className="bg-gray-100" data-id="element-743">
                                        <th className="border border-black p-2 text-left" data-id="element-744">
                                          Parameter
                                        </th>
                                        <th className="border border-black p-2 text-left" data-id="element-745">
                                          Result
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-746">
                                      {Object.entries(lab.results).map(([k, v]) => <tr key={k} data-id="element-747">
                                            <td className="border border-black p-2 font-medium" data-id="element-748">
                                              {k}
                                            </td>
                                            <td className="border border-black p-2" data-id="element-749">
                                              {v}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table> : <p className="text-gray-500 italic" data-id="element-750">
                                    No structured results recorded for this
                                    test.
                                  </p>}

                                {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-751">
                                    <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-752">
                                      Comments / Observations / Interpretation
                                    </h3>
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-753">
                                      {lab.labComments}
                                    </pre>
                                  </div>}
                              </>}
                          </div>;
              })()}

                    {rad && <div data-id="element-754">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-755">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-756">
                            <span className="font-semibold" data-id="element-757">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-758">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-759">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-760">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-761">
                              <h3 className="font-bold" data-id="element-762">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-763">
                                <button onClick={() => setImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-764">
                                  <ZoomOut className="w-4 h-4" data-id="element-765" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className="w-32" data-id="element-766" />
                                <button onClick={() => setImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-767">
                                  <ZoomIn className="w-4 h-4" data-id="element-768" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-769">
                                  {imageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-770">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-771">
                                  <div style={{
                        transform: `scale(${imageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-772">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-773" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-774">
      <div className="flex justify-between items-center" data-id="element-775">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-776">Patient Directory</h1>
      </div>

      <Card data-id="element-777">
        <div className="relative" data-id="element-778">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-779">
            <Search className="h-5 w-5 text-gray-400" data-id="element-780" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-781" />
        </div>
      </Card>

      <Card noPadding data-id="element-782">
        <div className="overflow-x-auto" data-id="element-783">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-784">
            <thead className="bg-gray-50" data-id="element-785">
              <tr data-id="element-786">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-787">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-788">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-789">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-790">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-791">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-792">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-793">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-794">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-795">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-796">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-797">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-798">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-799">
                        <FileText className="w-4 h-4 mr-2" data-id="element-800" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-801">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-802">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};